import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, LLM_TIMEOUT_MS } from "@/lib/api";
import { useAuthStore } from "@/lib/stores/auth";
import type {
  Assessment,
  AssessmentAuthInfo,
  Attempt,
  AttemptWithQuestionsResponse,
  ConnectedBookResponse,
  GapAnalysisDTO,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  User,
} from "@/types/api";

// Get all assessments available to the user (requires auth)
export function useAssessments(options?: { enabled?: boolean }) {
  return useQuery<Assessment[]>({
    queryKey: ["assessments"],
    queryFn: () => apiFetch<Assessment[]>("/assessments"),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get auth info for an assessment (public endpoint, no auth required)
// Used to determine which login flow to show (email vs Whop)
export function useAssessmentAuthInfo(assessmentId: number) {
  return useQuery<AssessmentAuthInfo>({
    queryKey: ["assessment-auth-info", assessmentId],
    queryFn: () => apiFetch<AssessmentAuthInfo>(`/assessments/${assessmentId}/auth-info`),
    enabled: !!assessmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes - auth info rarely changes
  });
}

// Get a single assessment with questions
export function useAssessment(id: number) {
  return useQuery<Assessment>({
    queryKey: ["assessment", id],
    queryFn: () => apiFetch<Assessment>(`/assessments/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes - assessments rarely change
  });
}

// Get user's attempts
export function useAttempts() {
  return useQuery<Attempt[]>({
    queryKey: ["attempts"],
    queryFn: () => apiFetch<Attempt[]>("/attempts"),
  });
}

// Get user's attempts for a specific assessment (newest first)
export function useAssessmentAttempts(assessmentId: number) {
  return useQuery<Attempt[]>({
    queryKey: ["assessment-attempts", assessmentId],
    queryFn: () => apiFetch<Attempt[]>(`/assessments/${assessmentId}/attempts`),
    enabled: !!assessmentId,
  });
}

// Get a single attempt with questions
export function useAttempt(id: number) {
  return useQuery<AttemptWithQuestionsResponse>({
    queryKey: ["attempt", id],
    queryFn: () => apiFetch<AttemptWithQuestionsResponse>(`/attempts/${id}`),
    enabled: !!id,
  });
}

// Fetch attempt directly (for use outside of React hooks)
export function fetchAttempt(id: number): Promise<AttemptWithQuestionsResponse> {
  return apiFetch<AttemptWithQuestionsResponse>(`/attempts/${id}`);
}

// Start a new attempt - returns attempt + questions
export function useStartAttempt() {
  const queryClient = useQueryClient();

  return useMutation<AttemptWithQuestionsResponse, Error, number>({
    mutationFn: (assessmentId) =>
      apiFetch<AttemptWithQuestionsResponse>(`/assessments/${assessmentId}/attempts`, {
        method: "POST",
      }),
    onSuccess: (_, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ["attempts"] });
      queryClient.invalidateQueries({ queryKey: ["assessment-attempts", assessmentId] });
    },
  });
}

// Submit answer request with attemptId included to avoid stale closures
export interface SubmitAnswerWithAttemptRequest extends SubmitAnswerRequest {
  attemptId: number;
}

// Submit an answer - attemptId is passed with each call to avoid stale closure issues
// Uses extended timeout for LLM-evaluated question types (code_review, refactor, hld, open_answer)
export function useSubmitAnswer() {
  return useMutation<SubmitAnswerResponse, Error, SubmitAnswerWithAttemptRequest>({
    mutationFn: ({ attemptId, ...data }) =>
      apiFetch<SubmitAnswerResponse>(`/attempts/${attemptId}/answers`, {
        method: "POST",
        body: JSON.stringify(data),
        timeout: LLM_TIMEOUT_MS, // 60 seconds for LLM evaluation
      }),
  });
}

// Complete an attempt
export function useCompleteAttempt() {
  const queryClient = useQueryClient();

  return useMutation<Attempt, Error, number>({
    mutationFn: (attemptId) =>
      apiFetch<Attempt>(`/attempts/${attemptId}/complete`, {
        method: "POST",
      }),
    onSuccess: (data, attemptId) => {
      queryClient.invalidateQueries({ queryKey: ["attempt", attemptId] });
      queryClient.invalidateQueries({ queryKey: ["attempts"] });
      queryClient.invalidateQueries({ queryKey: ["assessment-attempts", data.assessment_id] });
      // Book progress may have advanced via fire-and-forget
      queryClient.invalidateQueries({ queryKey: ["books"] });
      queryClient.invalidateQueries({ queryKey: ["book"] });
    },
  });
}

// User preferences update request
export interface UpdatePreferencesRequest {
  default_coding_language?: import("@/types/api").CodingLanguage;
  vim_motions_enabled?: boolean;
}

// Update user preferences via PATCH /auth/me
// Uses optimistic updates with rollback on error
export function useUpdateUserPreferences() {
  const updateUserOptimistic = useAuthStore((s) => s.updateUserOptimistic);

  return useMutation<void, Error, UpdatePreferencesRequest, { previousUser: User | null }>({
    mutationFn: (preferences) =>
      apiFetch<void>("/auth/me", {
        method: "PATCH",
        body: JSON.stringify(preferences),
      }),
    onMutate: (newPreferences) => {
      // Save current state for rollback
      const previousUser = useAuthStore.getState().user;
      // Optimistically update the UI immediately
      updateUserOptimistic(newPreferences);
      return { previousUser };
    },
    onError: (_err, _vars, context) => {
      // Rollback to previous state on error
      if (context?.previousUser) {
        useAuthStore.setState({ user: context.previousUser });
      }
      toast.error("Failed to save preferences");
    },
  });
}

// Returns the book linkage for an assessment (starting/finishing/chapter), or
// null when the assessment isn't tied to a book (server returns 204; apiFetch
// resolves an empty body to null). Drives the gap-analysis CTA variant.
export function useConnectedBook(
  assessmentId: number,
  options?: { enabled?: boolean }
) {
  return useQuery<ConnectedBookResponse | null>({
    queryKey: ["assessment-connected-book", assessmentId],
    queryFn: () =>
      apiFetch<ConnectedBookResponse | null>(
        `/assessments/${assessmentId}/connected-book`
      ),
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000, // 10 minutes; book linkage rarely changes
  });
}

// Triggers gap-analysis regeneration for a completed blind attempt that
// doesn't yet have one (or where the prior LLM call failed). On success,
// invalidates the attempt query so the populated gap_analysis lands on the
// next render.
export function useRetryGapAnalysis() {
  const queryClient = useQueryClient();

  return useMutation<GapAnalysisDTO, Error, number>({
    mutationFn: (attemptId) =>
      apiFetch<GapAnalysisDTO>(
        `/attempts/${attemptId}/retry-gap-analysis`,
        { method: "POST", timeout: LLM_TIMEOUT_MS }
      ),
    onSuccess: (_data, attemptId) => {
      queryClient.invalidateQueries({ queryKey: ["attempt", attemptId] });
    },
  });
}

// Backwards-compatible wrapper for language preference updates
export function useUpdateLanguagePreference() {
  const updatePrefs = useUpdateUserPreferences();

  return {
    ...updatePrefs,
    mutate: (language: import("@/types/api").CodingLanguage) =>
      updatePrefs.mutate({ default_coding_language: language }),
    mutateAsync: (language: import("@/types/api").CodingLanguage) =>
      updatePrefs.mutateAsync({ default_coding_language: language }),
  };
}
